import type { InputState } from '@/core/input';
import { type Facing, type KatieFrame } from '@/sprites/katie';
import { TILE_SIZE } from '@/sprites/tiles';
import { type Aabb, type Tilemap, moveAndCollide } from '@/world/tilemap';

/**
 * Katie's movement.
 *
 * Acceleration rather than instant velocity: it costs nothing and is the
 * difference between "controlling a character" and "dragging a cursor". The
 * collision box is deliberately smaller than the sprite and sits at her feet,
 * so her head can overlap trees without blocking her.
 */

export const WALK_SPEED = 72; // px/sec

/**
 * Three separate rates, because one rate cannot serve all three cases.
 *
 * A single acceleration value means reversing direction has to bleed off a full
 * WALK_SPEED and then build it again — twice the ramp, which reads as the
 * character ignoring you for a moment. Braking against the current direction is
 * therefore much faster than building speed in a fresh one.
 *
 * At 60fps: ~3 frames to full speed, ~3 to stop, ~3 to fully reverse.
 */
const ACCELERATION = 1400;
const FRICTION = 1600;
const TURN_ACCELERATION = 3200;

/** Feet-only collision box, in pixels, relative to the sprite's top-left. */
const BOX_OFFSET_X = 4;
const BOX_OFFSET_Y = 11;
const BOX_WIDTH = 8;
const BOX_HEIGHT = 5;

const WALK_CYCLE: readonly KatieFrame[] = ['walkA', 'idle', 'walkB', 'idle'];
const FRAME_DURATION = 0.14;

export interface PlayerSnapshot {
  readonly x: number;
  readonly y: number;
  readonly facing: Facing;
}

export class Player {
  /** Sprite top-left in world pixels. */
  x: number;
  y: number;
  /** Previous position, for render interpolation. */
  previousX: number;
  previousY: number;

  velocityX = 0;
  velocityY = 0;
  facing: Facing = 'down';

  private animationTime = 0;
  private moving = false;

  constructor(x: number, y: number) {
    this.x = x;
    this.y = y;
    this.previousX = x;
    this.previousY = y;
  }

  get box(): Aabb {
    return {
      x: this.x + BOX_OFFSET_X,
      y: this.y + BOX_OFFSET_Y,
      width: BOX_WIDTH,
      height: BOX_HEIGHT,
    };
  }

  /** Centre of the collision box — what the camera should track. */
  get centreX(): number {
    return this.x + BOX_OFFSET_X + BOX_WIDTH / 2;
  }

  get centreY(): number {
    return this.y + BOX_OFFSET_Y + BOX_HEIGHT / 2;
  }

  update(input: InputState, map: Tilemap, dt: number): void {
    this.previousX = this.x;
    this.previousY = this.y;

    const { move } = input;
    const targetX = move.x * WALK_SPEED;
    const targetY = move.y * WALK_SPEED;

    this.velocityX = approach(this.velocityX, targetX, rateFor(this.velocityX, targetX) * dt);
    this.velocityY = approach(this.velocityY, targetY, rateFor(this.velocityY, targetY) * dt);

    const box = this.box;
    const result = moveAndCollide(map, box, this.velocityX * dt, this.velocityY * dt);

    this.x = result.x - BOX_OFFSET_X;
    this.y = result.y - BOX_OFFSET_Y;

    // Zeroing velocity on impact stops her creeping along walls after release.
    if (result.hitX) this.velocityX = 0;
    if (result.hitY) this.velocityY = 0;

    this.updateFacing(move.x, move.y);

    const speed = Math.hypot(this.velocityX, this.velocityY);
    this.moving = speed > 4;
    this.animationTime = this.moving ? this.animationTime + dt : 0;
  }

  /**
   * Put her somewhere with no trail.
   *
   * The previous position follows and velocity dies, so the renderer does not
   * interpolate a streak across the map and she does not arrive still walking.
   */
  teleport(x: number, y: number): void {
    this.x = x;
    this.y = y;
    this.previousX = x;
    this.previousY = y;
    this.velocityX = 0;
    this.velocityY = 0;
  }

  /**
   * Shove her by a fixed amount, respecting walls.
   *
   * For movement the controller does not own — a dodge, and later knockback.
   * Deliberately separate from `update`: those forces do not accelerate,
   * decelerate or animate, and folding them into the walk would mean the walk
   * pretending to be something it is not.
   */
  displace(map: Tilemap, dx: number, dy: number): void {
    const result = moveAndCollide(map, this.box, dx, dy);
    this.x = result.x - BOX_OFFSET_X;
    this.y = result.y - BOX_OFFSET_Y;
  }

  /**
   * Vertical facing wins ties so that walking diagonally reads as up/down,
   * matching how the sprite set is weighted.
   */
  private updateFacing(moveX: number, moveY: number): void {
    if (moveX === 0 && moveY === 0) return;
    if (Math.abs(moveY) >= Math.abs(moveX)) {
      this.facing = moveY < 0 ? 'up' : 'down';
    } else {
      this.facing = moveX < 0 ? 'left' : 'right';
    }
  }

  get frame(): KatieFrame {
    if (!this.moving) return 'idle';
    const index = Math.floor(this.animationTime / FRAME_DURATION) % WALK_CYCLE.length;
    return WALK_CYCLE[index]!;
  }

  /** Tile the feet currently occupy, for interaction checks later. */
  get tileX(): number {
    return Math.floor(this.centreX / TILE_SIZE);
  }

  get tileY(): number {
    return Math.floor(this.centreY / TILE_SIZE);
  }

  snapshot(): PlayerSnapshot {
    return { x: this.x, y: this.y, facing: this.facing };
  }
}

/**
 * Which rate applies this step: releasing, reversing, or building speed.
 * Exported so the tuning is testable rather than only felt.
 */
export function rateFor(current: number, target: number): number {
  if (target === 0) return FRICTION;
  if (current !== 0 && Math.sign(current) !== Math.sign(target)) return TURN_ACCELERATION;
  return ACCELERATION;
}

/** Move `current` toward `target` by at most `maxDelta`. */
export function approach(current: number, target: number, maxDelta: number): number {
  if (current < target) return Math.min(current + maxDelta, target);
  if (current > target) return Math.max(current - maxDelta, target);
  return target;
}
